import "./SimulationPerformanceChartComponent.scss";
import {IComponentOptions, IOnChanges, IOnChangesObject, IOnDestroy, IOnInit, IRootElementService} from "angular";
import {SystemEvent} from "../../../services/system-state/SystemEvent";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {Subscription} from "rxjs/Subscription";
import {Optional} from "../../../js/util/Optional";
import {SimulationCalculationRecord} from "../../models/TradeSimulationRecords";
import CONFIG from "./defaults/ChartConfig";
import {generateDefaultChartOptions} from "./defaults/DefaultChartOptions";

export const PerformanceChart = "tfSimulationPerformanceChart";

export function performanceChart(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/performance-chart/SimulationPerformanceChartComponent.html",
        controllerAs: "ctrl",
        controller: SimulationPerformanceChartComponent,
        bindings: {
            data: "<"
        }
    };
}

export class SimulationPerformanceChartComponent implements IOnInit, IOnDestroy, IOnChanges {

    public data: Array<SimulationCalculationRecord>;
    public chartData: NVD3ChartData;
    public chartOptions: any;
    public chartConfig = CONFIG;

    private api: any;

    private stateSub = Subscription.EMPTY;

    /** @ngInject */
    constructor(private systemEventService: SystemEventService) {
    }

    public $onInit(): void {
        this.stateSub = this.systemEventService.subscribe(this.onSystemEvent.bind(this));
    }

    private onSystemEvent(event: SystemEvent) {
        if (event === SystemEvent.WINDOW_BLUR) {
            this.hideTooltip();
        } else if (event === SystemEvent.VIEW_RESIZE) {
            this.refresh();
        }
    }

    public chartReady($scope: any, $elem: IRootElementService) {
        this.api = $scope.api;
    }

    private refreshChart(data: Array<SimulationCalculationRecord>) {
        if (data != null) {
            this.ensureTooltipRemoved();
            this.chartData = this.generateChartData(data);
            this.chartOptions = this.generateChartOptions(data);
        }
    }

    private generateChartOptions(data: Array<SimulationCalculationRecord>) {
        const defaults = generateDefaultChartOptions(500);
        return Object.assign({}, defaults);
    }

    private generateChartData(data: Array<SimulationCalculationRecord>): NVD3ChartData {
        const values = data.map(d => {
            const asAt = d.asAt;
            const pnl = d.pnl;
            return {x: new Date(asAt), y: pnl};
        });

        return [{
            key: "Profit / Loss",
            values
        }];
    }

    public $onChanges(changes: IOnChangesObject): void {
        this.refreshChart(this.data);
    }

    private refresh() {
        Optional.of(this.api).ifPresent(api => api.update());
    }

    private hideTooltip() {
        this.findCurrentTooltip()
            .ifPresent(tt => tt.hidden(true));
    }

    // angular-nv doesn't clean up after itself properly, so we have to make sure the old tooltip is gone
    private ensureTooltipRemoved() {
        this.findCurrentTooltip()
            .map(tooltip => tooltip.id())
            .map(id => document.querySelector(`#${id}`))
            .ifPresent(el => el.remove());
    }

    private findCurrentTooltip() {
        return Optional.of(this.api)
                       .map(api => api.getScope())
                       .map(scope => scope.chart)
                       .map(chart => chart.interactiveLayer)
                       .map(layer => layer.tooltip);
    }

    public $onDestroy() {
        this.ensureTooltipRemoved();
        this.stateSub.unsubscribe();
    }
}
